import React from 'react';
import type { ShowcaseWithHero } from '../services/showcases';
import { isShowcaseLive } from '../types';
import { ExternalLink, Pencil, Trash2, Copy, BarChart3, MessageSquare, MoreVertical, Image } from 'lucide-react';
import { SHOWCASE_BASE_URL } from '../constants';

interface ShowcaseCardProps {
  showcase: ShowcaseWithHero;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onViewLeads: () => void;
  onViewAnalytics: () => void;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Draft' },
  published: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Published' },
  archived: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: 'Archived' },
  expired: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Expired' },
};

export default function ShowcaseCard({ showcase, onEdit, onDelete, onDuplicate, onViewLeads, onViewAnalytics }: ShowcaseCardProps) {
  const [showMenu, setShowMenu] = React.useState(false);
  const isLive = isShowcaseLive(showcase);
  const statusKey = showcase.status === 'published' && !isLive ? 'expired' : showcase.status;
  const statusStyle = STATUS_STYLES[statusKey] || STATUS_STYLES.draft;
  const viewUrl = `${SHOWCASE_BASE_URL}/view/${showcase.slug}`;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors group">
      <div
        className="aspect-[16/9] bg-slate-800 relative overflow-hidden cursor-pointer"
        onClick={onEdit}
      >
        {showcase.hero_url ? (
          <img
            src={showcase.hero_url}
            alt={showcase.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-cyan-900/20 to-blue-900/20 flex items-center justify-center">
            <Image className="w-12 h-12 text-slate-700" />
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
            {statusStyle.label}
          </span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-white mb-1 truncate">{showcase.title || 'Untitled Showcase'}</h3>
        <p className="text-sm text-slate-400 mb-3">
          Updated {new Date(showcase.updated_at).toLocaleDateString()}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            {isLive && (
              <a
                href={viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors flex items-center gap-1.5"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                View
              </a>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 bottom-full mb-1 bg-slate-800 border border-slate-700 rounded-xl py-1 w-44 z-20 shadow-xl">
                  <button onClick={() => { onDuplicate(); setShowMenu(false); }} className="w-full px-3 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2">
                    <Copy className="w-3.5 h-3.5" /> Duplicate
                  </button>
                  <button onClick={() => { onViewLeads(); setShowMenu(false); }} className="w-full px-3 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5" /> View Leads
                  </button>
                  <button onClick={() => { onViewAnalytics(); setShowMenu(false); }} className="w-full px-3 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 flex items-center gap-2">
                    <BarChart3 className="w-3.5 h-3.5" /> Analytics
                  </button>
                  <div className="border-t border-slate-700 my-1" />
                  <button onClick={() => { onDelete(); setShowMenu(false); }} className="w-full px-3 py-2 text-sm text-left text-red-400 hover:bg-slate-700 flex items-center gap-2">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
